import { useGetAnalyticsSummary } from '@reelplicate/api-client/analytics';
import type { AnalyticsSummaryDto, WeekStatDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Skeleton } from '@reelplicate/ui';

import { formatCount } from '@/features/analytics/format';

import { type Stat, StatTile } from './stat-tile';

const sign = (n: number) => (n < 0 ? '−' : '+');

/** "+31%" vs last week, or null without a base to compare with. */
function percent({ value, previous }: WeekStatDto): Pick<Stat, 'delta' | 'up'> {
  if (value === null || !previous) return { delta: null, up: true };
  const change = Math.round(((value - previous) / previous) * 100);
  return { delta: `${sign(change)}${Math.abs(change)}%`, up: change >= 0 };
}

/** "+4" / "+2.1s" vs last week. */
function difference(
  { value, previous }: WeekStatDto,
  unit = '',
): Pick<Stat, 'delta' | 'up'> {
  if (value === null || previous === null) return { delta: null, up: true };
  const change = Math.round((value - previous) * 10) / 10;
  return { delta: `${sign(change)}${Math.abs(change)}${unit}`, up: change >= 0 };
}

function weekStats(summary: AnalyticsSummaryDto): Stat[] {
  const { views, reelsPublished, avgWatchSeconds } = summary;
  return [
    {
      label: 'Views this week',
      value: views.value === null ? '—' : formatCount(views.value, 1_000),
      ...percent(views),
    },
    {
      label: 'Reels published',
      value: String(reelsPublished.value ?? 0),
      ...difference(reelsPublished),
    },
    {
      label: 'Avg. watch time',
      value: avgWatchSeconds.value === null ? '—' : `${avgWatchSeconds.value}s`,
      ...difference(avgWatchSeconds, 's'),
    },
  ];
}

/** Home's "This week" tiles, from the account stats and the Calendar. */
export function ThisWeek() {
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const { data: summary } = useGetAnalyticsSummary(workspaceId, {
    query: { enabled: Boolean(workspaceId), throwOnError: false },
  });

  return (
    <section
      aria-label="This week"
      className="bg-bg-surface border-border-subtle rounded-2xl border p-1"
    >
      {summary ? (
        <dl className="divide-border-subtle flex flex-col divide-y sm:flex-row sm:divide-x sm:divide-y-0">
          {weekStats(summary).map((stat) => (
            <StatTile key={stat.label} stat={stat} />
          ))}
        </dl>
      ) : (
        <Skeleton shape="rectangle" shimmer className="h-[116px] rounded-xl" />
      )}
    </section>
  );
}
